import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { adminButton, adminFocusable, adminText, adminTokens } from '../../admin-ui';
import {
  assignCourseTag,
  assignLessonTag,
  tagKeys,
  unassignCourseTag,
  unassignLessonTag,
  useCourseTagsQuery,
  useLessonTagsQuery,
  useTagsQuery,
  type TagSummary,
} from '../../api/tagApi';
import { courseRecordKeys, lessonListKeys } from '../../api/courseRecordApi';
import { useClientContext } from '../../hooks/useClientContext';
import { ModalShell } from '../ModalShell';
import { TagPickerView } from './TagPickerView';

export type EditTagsContentType = 'lesson' | 'course';

export interface EditTagsDialogProps {
  isOpen: boolean;
  contentType: EditTagsContentType;
  itemId: string;
  itemTitle: string;
  onClose: () => void;
  /** Called after a save settles successfully; `changed` is false when nothing differed. */
  onSaved: (result: { changed: boolean }) => void;
}

/**
 * Lessons/Courses list "Edit tags" dialog (NA-480). Unlike the metadata
 * screens' pickers, which save every change as it is made, edits here live in
 * a local draft and are only applied — as the difference from the item's saved
 * tags — when Save is clicked.
 */
export function EditTagsDialog({ isOpen, contentType, itemId, itemTitle, onClose, onSaved }: EditTagsDialogProps) {
  const titleId = useId();
  // Lifted out of the body (which unmounts on close) so Escape/backdrop cannot
  // dismiss the dialog while a save is in flight.
  const [isSaving, setIsSaving] = useState(false);

  return (
    <ModalShell
      isOpen={isOpen}
      onClose={isSaving ? () => undefined : onClose}
      ariaLabelledBy={titleId}
      width="480px"
      // A stray click outside must not silently discard unsaved edits.
      disableBackdropClose
      // Opened only from the Lessons/Courses lists. The tag picker inside is the
      // editor's shared control and keeps its own presentation.
      presentation="admin"
    >
      <EditTagsDialogBody
        titleId={titleId}
        contentType={contentType}
        itemId={itemId}
        itemTitle={itemTitle}
        isSaving={isSaving}
        setIsSaving={setIsSaving}
        onClose={onClose}
        onSaved={onSaved}
      />
    </ModalShell>
  );
}

interface EditTagsDialogBodyProps extends Omit<EditTagsDialogProps, 'isOpen'> {
  titleId: string;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
}

function EditTagsDialogBody({
  titleId,
  contentType,
  itemId,
  itemTitle,
  isSaving,
  setIsSaving,
  onClose,
  onSaved,
}: EditTagsDialogBodyProps) {
  const styles = useMemo(() => createStyles(), []);
  const queryClient = useQueryClient();
  const { clientContext } = useClientContext();
  const bodyRef = useRef<HTMLDivElement | null>(null);

  // Only the query for this content type is enabled; the other gets a null id.
  const lessonTags = useLessonTagsQuery(contentType === 'lesson' ? itemId : null);
  const courseTags = useCourseTagsQuery(contentType === 'course' ? itemId : null);
  const itemTags = contentType === 'lesson' ? lessonTags : courseTags;
  const { data: library } = useTagsQuery({ assignedAccountId: clientContext?.id });

  const saved = itemTags.data?.items;
  const isSettled = saved !== undefined && !itemTags.isFetching;

  // The draft is seeded once, from fresh server data, and is never overwritten
  // by later refetches. The diff below is always draft vs. the *current* saved
  // tags, so after a partial failure the refetch alone narrows a retry to what
  // is still outstanding.
  const [draftIds, setDraftIds] = useState<number[] | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (draftIds === null && isSettled) setDraftIds(saved.map((tag) => tag.id));
  }, [draftIds, isSettled, saved]);

  // The focus trap lands on the first enabled control (the close button) at
  // open; once the picker is usable, move focus to its input.
  const isReady = draftIds !== null;
  useEffect(() => {
    if (isReady) bodyRef.current?.querySelector<HTMLInputElement>('input[type="text"]')?.focus();
  }, [isReady]);

  const chips: TagSummary[] = useMemo(() => {
    const byId = new Map<number, TagSummary>();
    for (const tag of saved ?? []) byId.set(tag.id, { id: tag.id, name: tag.name });
    for (const tag of library?.items ?? []) byId.set(tag.id, { id: tag.id, name: tag.name });
    return (draftIds ?? []).flatMap((id) => {
      const tag = byId.get(id);
      return tag ? [tag] : [];
    });
  }, [draftIds, library, saved]);

  const savedIds = useMemo(() => (saved ?? []).map((tag) => tag.id), [saved]);
  const adds = (draftIds ?? []).filter((id) => !savedIds.includes(id));
  const removes = savedIds.filter((id) => !(draftIds ?? []).includes(id));

  const handleAssign = useCallback((tagId: number) => {
    setDraftIds((ids) => (ids === null || ids.includes(tagId) ? ids : [...ids, tagId]));
  }, []);
  const handleUnassign = useCallback((tagId: number) => {
    setDraftIds((ids) => (ids === null ? ids : ids.filter((id) => id !== tagId)));
  }, []);

  const handleSave = async () => {
    if (!isReady || isSaving) return;
    setSaveError(null);
    if (adds.length === 0 && removes.length === 0) {
      onSaved({ changed: false });
      return;
    }

    setIsSaving(true);
    const assign = contentType === 'lesson' ? assignLessonTag : assignCourseTag;
    const unassign = contentType === 'lesson' ? unassignLessonTag : unassignCourseTag;
    // Different join rows, so the calls are independent; settle them all so one
    // failure cannot hide which others landed.
    const results = await Promise.allSettled([
      ...adds.map((tagId) => assign(itemId, tagId)),
      ...removes.map((tagId) => unassign(itemId, tagId)),
    ]);

    // One refresh per save (not per tag): the item's saved tags (moves the diff
    // baseline to what actually persisted), usage counts, and both list caches.
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: contentType === 'lesson' ? tagKeys.lessonTags(itemId) : tagKeys.courseTags(itemId),
      }),
      queryClient.invalidateQueries({ queryKey: tagKeys.lists() }),
      queryClient.invalidateQueries({ queryKey: lessonListKeys.all }),
      queryClient.invalidateQueries({ queryKey: courseRecordKeys.lists() }),
    ]);

    setIsSaving(false);
    if (results.every((result) => result.status === 'fulfilled')) {
      onSaved({ changed: true });
    } else {
      setSaveError("Some tag changes couldn't be saved. Your selections are still here, so you can try again.");
    }
  };

  const loadFailed = itemTags.isError && !isReady;

  return (
    <div ref={bodyRef} style={styles.body}>
      <div style={styles.header}>
        <div style={styles.headerText}>
          <h3 id={titleId} style={styles.title}>Edit tags</h3>
          <p style={styles.itemName}>{itemTitle}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          disabled={isSaving}
          style={styles.closeButton}
          aria-label="Close without saving"
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      </div>

      <TagPickerView
        tags={chips}
        allTags={library?.items ?? []}
        countKey={contentType === 'lesson' ? 'lessonCount' : 'courseCount'}
        onAssign={handleAssign}
        onUnassign={handleUnassign}
        disabled={!isReady || isSaving}
        // Focus lands in the input on open; the list would cover Cancel and Save.
        openOnFocus={false}
      />

      {!isReady && !loadFailed && <p role="status" style={styles.hint}>Loading tags...</p>}
      {loadFailed && <p role="alert" style={styles.error}>Couldn&apos;t load this item&apos;s tags. Close this dialog and try again.</p>}
      {saveError && <p role="alert" style={styles.error}>{saveError}</p>}

      <div style={styles.actions}>
        <button
          type="button"
          onClick={onClose}
          disabled={isSaving}
          style={{ ...styles.cancelButton, ...(isSaving ? styles.buttonDisabled : {}) }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => { void handleSave(); }}
          disabled={!isReady || isSaving}
          aria-busy={isSaving}
          style={{ ...styles.saveButton, ...(!isReady || isSaving ? styles.buttonDisabled : {}) }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function createStyles(): Record<string, CSSProperties> {
  const a = adminTokens;
  return {
    body: {
      padding: a.spacing.xl,
      display: 'flex',
      flexDirection: 'column',
      gap: a.spacing.md,
      // ModalShell clips overflow, so reserve room for the picker's dropdown
      // (field bottom sits ~150px down, list up to ~262px tall) instead of
      // letting it be cut off at the dialog's edge.
      minHeight: '440px',
      boxSizing: 'border-box',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: a.spacing.md,
    },
    headerText: {
      minWidth: 0,
    },
    title: adminText.sectionTitle,
    itemName: {
      margin: '4px 0 0 0',
      fontSize: a.typography.body.size,
      color: a.text.secondary,
      overflowWrap: 'anywhere',
    },
    closeButton: {
      ...adminFocusable,
      flexShrink: 0,
      width: '32px',
      height: '32px',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'none',
      border: 'none',
      borderRadius: a.radius.control,
      padding: 0,
      cursor: 'pointer',
      fontSize: '16px',
      color: a.text.secondary,
    },
    hint: {
      margin: 0,
      fontSize: a.typography.label.size,
      color: a.text.secondary,
    },
    error: {
      margin: 0,
      fontSize: a.typography.body.size,
      color: a.status.error.text,
      lineHeight: 1.5,
    },
    actions: {
      display: 'flex',
      justifyContent: 'flex-end',
      gap: a.spacing.md,
      marginTop: 'auto',
    },
    cancelButton: adminButton.secondary,
    saveButton: adminButton.primary,
    buttonDisabled: adminButton.disabled,
  };
}
